import { Link } from 'react-router-dom';

const steps = [
  {
    number: '01',
    title: 'Free Consultation',
    desc: 'We start with a no-obligation on-site consultation — understanding your vision, measuring your property, and discussing every detail of your project goals.',
    icon: 'ri-chat-check-line',
    detail: 'Typically 30–60 minutes on-site',
  },
  {
    number: '02',
    title: 'Transparent Proposal',
    desc: 'You receive a detailed, itemized estimate. Every material, milestone, and timeline is spelled out clearly — no hidden fees, no surprises at completion.',
    icon: 'ri-file-list-3-line',
    detail: 'Delivered within 48 hours',
  },
  {
    number: '03',
    title: 'Expert Installation',
    desc: 'Our licensed crew arrives on time and executes your project with precision. We protect your property, stay on schedule, and communicate every step of the way.',
    icon: 'ri-tools-line',
    detail: 'On-time, every time',
  },
  {
    number: '04',
    title: 'Walk-Through & Sign-Off',
    desc: 'We won\'t call it done until you do. Our final walk-through ensures every element of your outdoor space exceeds your expectations — guaranteed.',
    icon: 'ri-checkbox-circle-line',
    detail: '100% satisfaction required',
  },
];

export default function ProcessSection() {
  return (
    <section className="relative bg-charcoal py-28 px-6 overflow-hidden">
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(circle, #C9962A 1px, transparent 1px)',
          backgroundSize: '36px 36px',
        }}
      ></div>

      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-gold font-sans text-[11px] font-semibold tracking-[0.28em] uppercase mb-4 flex items-center gap-3 justify-center">
            <span className="h-px w-8 bg-gold inline-block"></span>
            How It Works
            <span className="h-px w-8 bg-gold inline-block"></span>
          </p>
          <h2 className="font-serif text-white font-bold mb-4" style={{ fontSize: 'clamp(32px, 3.5vw, 50px)' }}>
            A Process Built on Trust
          </h2>
          <p className="text-white/40 text-base max-w-lg mx-auto">
            From your first call to the final walk-through — a seamless, professional experience with clear communication at every stage.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((step, i) => (
            <div key={i} className="relative group">
              {/* Connecting line */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-14 left-[calc(100%+10px)] w-[calc(100%-20px)] h-px bg-gradient-to-r from-gold/25 to-transparent z-0"></div>
              )}

              <div className="bg-white/4 border border-white/8 rounded-2xl p-7 h-full hover:bg-white/7 hover:border-gold/20 transition-all duration-300 group">
                {/* Number */}
                <span className="font-serif text-6xl font-bold text-white/7 group-hover:text-gold/12 transition-colors leading-none block mb-4">
                  {step.number}
                </span>

                {/* Icon circle */}
                <div className="w-12 h-12 flex items-center justify-center bg-gold/10 border border-gold/20 rounded-xl mb-5">
                  <i className={`${step.icon} text-gold text-lg`}></i>
                </div>

                <h3 className="font-serif text-white text-xl font-semibold mb-3">{step.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed mb-5">{step.desc}</p>

                <div className="flex items-center gap-2 pt-4 border-t border-white/8">
                  <i className="ri-time-line text-gold/60 text-xs"></i>
                  <span className="text-gold/60 text-xs font-medium tracking-wide">{step.detail}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-gold text-charcoal font-bold text-base px-10 py-4 rounded-full hover:bg-gold-light transition-colors whitespace-nowrap"
          >
            <i className="ri-pencil-ruler-2-line"></i>
            Start with a Free Consultation
          </Link>
          <a
            href="tel:3863014399"
            className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-base px-8 py-4 rounded-full hover:border-white/50 transition-colors whitespace-nowrap"
          >
            <i className="ri-phone-fill text-gold"></i>
            (386) 301-4399
          </a>
        </div>
        <p className="text-center text-white/25 text-sm mt-4">Mon–Fri 9AM–3PM &nbsp;·&nbsp; Sat by Appointment</p>
      </div>
    </section>
  );
}
